/**
 * Escalation model — types, DTOs, domain model, and mapper for BDP-45.
 * Pure domain module: no React, no API imports.
 */

// ---------------------------------------------------------------------------
// Tier types
// ---------------------------------------------------------------------------

export type TierLabel = 'PM' | 'DL' | 'OWNER' | 'CL' | 'CONFIRMED';

export const TIER_LABELS: Record<TierLabel, string> = {
  PM: 'PM Notified',
  DL: 'Delivery Lead Notified',
  OWNER: 'Deal Owner Notified',
  CL: 'Client Lead Notified',
  CONFIRMED: 'Confirmed',
};

// ---------------------------------------------------------------------------
// Wire DTOs — shape returned by GET .../escalation-history
// ---------------------------------------------------------------------------

export interface EscalationRecipientDTO {
  name: string;
  email: string;
}

export interface EscalationLogEntryDTO {
  tier: string;
  notified_date: string;
  recipient_count: number;
  recipients: EscalationRecipientDTO[];
}

export interface EscalationHistoryResponseDTO {
  entries: EscalationLogEntryDTO[];
  latest_tier: string | null;
  cycle_start_date: string;
}

// ---------------------------------------------------------------------------
// Domain model — camelCase, used by hooks and components
// ---------------------------------------------------------------------------

export interface EscalationRecipient {
  name: string;
  email: string;
}

export interface EscalationLogEntry {
  tier: TierLabel;
  notifiedDate: string;
  recipientCount: number;
  recipients?: EscalationRecipient[];
}

export interface EscalationHistory {
  entries: EscalationLogEntry[];
  latestTier: TierLabel | null;
  cycleStartDate: string;
}

// ---------------------------------------------------------------------------
// Mapper
// ---------------------------------------------------------------------------

const KNOWN_TIERS = new Set<string>(['PM', 'DL', 'OWNER', 'CL', 'CONFIRMED']);

function toTierLabel(raw: string): TierLabel {
  const upper = raw.toUpperCase();
  return KNOWN_TIERS.has(upper) ? (upper as TierLabel) : 'PM';
}

export function mapEscalationHistoryDTO(dto: EscalationHistoryResponseDTO): EscalationHistory {
  return {
    entries: dto.entries.map((e) => ({
      tier: toTierLabel(e.tier),
      notifiedDate: e.notified_date,
      recipientCount: e.recipient_count,
      recipients: e.recipients ?? [],
    })),
    latestTier: dto.latest_tier ? toTierLabel(dto.latest_tier) : null,
    cycleStartDate: dto.cycle_start_date,
  };
}
